import {
  useQuery,
  useMutation,
  type UseMutationOptions,
  type UseQueryOptions,
  type UseMutationResult,
} from '@tanstack/react-query';
import axios, { type AxiosRequestConfig, type AxiosResponse } from 'axios';
import { authStore } from '@/store/authStore';
import { useFunnelStore } from '@/store/funnelStore';
import { trackError } from '@/utils/trackError';
import type { FunnelNavigationResponse } from '@/types/api/shared';

export interface ApiError {
  type: string;
  message: string;
  status?: number;
  warning?: boolean;
  verified?: boolean;
  data?: FunnelNavigationResponse;
}

interface FetchDataOptions<TData = unknown>
  extends Omit<UseQueryOptions<TData, ApiError>, 'queryKey' | 'queryFn'> {
  key: string | (string | number | null)[];
  url: string;
  params?: Record<string, unknown>;
}

interface HttpRequestParams {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  url: string;
  data?: unknown;
  params?: Record<string, unknown>;
}

const apiClient = axios.create({
  baseURL: `${import.meta.env['VITE_API_URL']}/api`,
  withCredentials: true,
});

apiClient.interceptors.request.use(
  (config) => {
    const { accessToken } = authStore.getState();
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

let refreshPromise: Promise<void> | null = null;

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { clearTokens } = authStore.getState();
    const originalRequest = error.config;

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !['/login'].some((authPath) => originalRequest.url.includes(authPath))
    ) {
      originalRequest._retry = true;

      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }

      try {
        await refreshPromise;
        return apiClient(originalRequest);
      } catch (error: unknown) {
        const isValidateSession = originalRequest?.url?.includes('/validate_session');

        const err = error as { response?: { status?: number; data?: { valid?: boolean } } };

        const isExplicitlyInvalid =
          err?.response?.status === 401 || err?.response?.data?.valid === false;

        if (isValidateSession && isExplicitlyInvalid) {
          return Promise.reject(error);
        }
        clearTokens();
        document.dispatchEvent(new Event('logout'));
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);

const refreshAccessToken = async () => {
  try {
    const response = await axios.post(
      `${import.meta.env['VITE_API_URL']}/oauth/token`,
      { grant_type: 'refresh_token' },
      { withCredentials: true },
    );
    const accessToken = response.headers['access-token'];
    if (!accessToken) {
      const err = new Error('No access token found in headers');
      trackError(err, 'refreshAccessToken', { extra: { headers: response.headers } });
      throw err;
    }

    authStore.getState().setTokens(accessToken);
  } catch (error: unknown) {
    trackError(error, 'refreshAccessToken');
    throw error;
  }
};

export function httpRequest<T>(args: HttpRequestParams, includeHeaders?: false): Promise<T>;

export function httpRequest<T>(
  args: HttpRequestParams,
  includeHeaders: boolean,
): Promise<{ data: T; _headers: AxiosResponse['headers']; accessToken?: string }>;

export async function httpRequest<T>(
  { method, url, data, params }: HttpRequestParams,
  includeHeaders = false,
): Promise<T | { data: T; _headers: AxiosResponse['headers']; accessToken?: string }> {
  try {
    const origin = window.location.origin;
    const currentPath = localStorage.getItem('currentPath') || '';
    const prevPath = localStorage.getItem('prevPath') || '';

    const response: AxiosResponse = await apiClient({
      method,
      url,
      data,
      params,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        WEBSITE_FULL_URL: `${origin}${currentPath}`,
        WEBSITE_REFFERER: `${origin}${prevPath}`,
        WEBSITE_USER_AGENT: navigator.userAgent,
      },
    } as AxiosRequestConfig);

    const responseData = response.data as T;
    const headers = response.headers;
    const accessToken = headers['access-token'];
    const impersonating = headers['impersonating'];

    if (includeHeaders) {
      return {
        data: accessToken ? { ...responseData, accessToken, impersonating } : responseData,
        _headers: headers,
      } as {
        data: T;
        _headers: AxiosResponse['headers'];
      };
    }

    return accessToken ? { ...responseData, accessToken, impersonating } : responseData;
  } catch (err: unknown) {
    const error = err as {
      response: {
        status: number;
        data: {
          errors: {
            message: string;
            warning?: boolean;
            verified?: boolean;
            next_path?: FunnelNavigationResponse;
          };
          error: string;
          message: string;
        };
      };
    };

    const errorStatus = error.response?.status || 500;
    const errorMsg =
      error.response?.data.errors?.message ||
      error.response?.data.errors ||
      error.response?.data.message ||
      'Something went wrong. Please try again later.';
    const warning = error.response?.data.errors?.warning || false;

    if (errorStatus === 401) {
      // Don't track 401 errors to Sentry as these are expected when sessions expire
      // The interceptor handles token refresh and logout appropriately
      throw {
        type: 'unauthorized',
        message: errorMsg,
        status: errorStatus,
        warning: warning,
      } as ApiError;
    }

    if (errorStatus === 403) {
      const prevPath = localStorage.getItem('prevPath');
      useFunnelStore
        .getState()
        .setAccessDeniedMessage({ message: errorMsg as string, nextPath: prevPath || '' });
      throw { type: 'forbidden', message: errorMsg, status: errorStatus } as ApiError;
    }

    if (errorStatus === 422) {
      throw {
        type: 'unprocessableEntity',
        message: errorMsg,
        status: errorStatus,
        verified: error.response?.data.errors?.verified,
        data: error.response?.data.errors?.next_path,
      } as ApiError;
    }

    // Track errors to Sentry, but skip 401 errors (handled above before trackError is called)
    trackError(err, 'httpRequest', { extra: { method, url, params, status: errorStatus } });

    if (errorStatus === 400) {
      throw { type: 'badRequest', message: errorMsg, status: errorStatus } as ApiError;
    } else if (errorStatus === 404) {
      throw { type: 'notFound', message: errorMsg, status: errorStatus } as ApiError;
    } else if (errorStatus === 500) {
      throw {
        type: 'serverError',
        message: 'Something went wrong. Please try again later.',
        status: errorStatus,
      } as ApiError;
    } else {
      throw { type: 'error', message: errorMsg, status: errorStatus } as ApiError;
    }
  }
}

export const useFetchData = <TData = unknown>({
  key,
  url,
  params,
  ...options
}: FetchDataOptions<TData>) => {
  return useQuery<TData, ApiError>({
    queryKey: Array.isArray(key) ? key : [key],
    queryFn: () => httpRequest<TData>({ method: 'GET', url, params }),
    ...options,
  });
};

export function useMutateData<TData, TVariables = unknown, TReturn = TData>(
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
  url: string,
  options?: UseMutationOptions<TReturn, ApiError, TVariables>,
  includeHeaders = false,
): UseMutationResult<TReturn, ApiError, TVariables> {
  return useMutation<TReturn, ApiError, TVariables>({
    mutationFn: async (data: TVariables) => {
      return (await httpRequest<TData>({ method, url, data }, includeHeaders)) as TReturn;
    },
    onError: (error, variables, context) => {
      trackError(error, 'useMutateData', { extra: { method, url } });
      options?.onError?.(error, variables, context);
    },
    ...options,
  });
}
